<template>
  <transition
    v-if="active"
    name="modal"
  >
    <div class="base-upsell-modal-component">
      <div class="backdrop">
        <div class="content">
          <div class="tagline-container">
            <h2 class="tagline">
              Start Now,<br />Get 72 Hours Free
            </h2>
            <hlt-label-text
              class="sub-tagline"
              :size="'sm'"
            >
              Give us a chance to prove we’re the best investment you’ll ever make.
            </hlt-label-text>
          </div>
          <div class="purchase-options">
            <div class="tabs">
              <button
                v-for="purchaseOrder in purchaseOrders"
                :key="purchaseOrder.id"
                class="purchase-order-tab"
                :class="{ selected: selectedPurchaseOrder && purchaseOrder.id === selectedPurchaseOrder.id }"
                @click="setSelectedPurchaseOrder(purchaseOrder)"
              >
                {{ purchaseOrder.name }}
              </button>
            </div>
            <div
              v-for="( purchaseOrder, index ) in purchaseOrders"
              :key="purchaseOrder.id"
              class="purchase-details"
              :class="{ hidden: selectedPurchaseOrder.id !== purchaseOrder.id, preferred: preferredPurchaseOrder.id === purchaseOrder.id }"
            >
              <p class="purchase-name">
                {{ purchaseOrder.name }}
              </p>
              <div
                class="purchase-order"
                v-html="purchaseOrder.descriptionHtml"
              />
              <p class="pricing">
                {{ purchaseOrder.costString }}<span class="frequency-text">/month</span>
              </p>
              <p
                v-if="!userIsSubscribed"
                class="trial-text"
              >
                First 3 days free. Cancel any time.
              </p>
              <purchase-form
                ref="submitForm"
                :action="legacyLink"
                :partner-ref="partnerRef"
                :purchase-order="purchaseOrder"
                :sc="sc"
                :user="user"
              >
                <button
                  class="action-button"
                  :class="{ inverted: purchaseOrder.id !== preferredPurchaseOrder.id }"
                  :disabled="userHasActivePO(purchaseOrder)"
                  @click.prevent="triggerSubmit(index)"
                >
                  {{ actionButtonText(purchaseOrder) }}
                </button>
              </purchase-form>
            </div>
          </div>
          <button
            class="close"
            @click="hide"
          >
            <icon
              :name="'cross-thin'"
            />
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Icon from '@/components/singletons/icon.vue';
import HltLabelText from '@/components/ui/label-text.vue';
import PurchaseForm from '@/components/singletons/purchaseForm.vue';

import { legacyFormLink } from '@/utils';

export default {
  name: 'BaseUpsellModal',
  components: {
    Icon,
    HltLabelText,
    PurchaseForm,
  },
  data() {
    return {
      active: false,
      selectedPurchaseOrder: null,
      partnerRef: undefined,
      sc: undefined,
      selected: {},
    };
  },
  computed: {
    legacyLink() {
      return legacyFormLink(this.$store);
    },
    listener() {
      return this.$store.getters['ui/baseUpsellModalStatus'];
    },
    preferredPurchaseOrder() {
      // TODO: This should be based on the current subscription if it exists
      return this.purchaseOrders?.[this.purchaseOrders?.length - 1];
    },
    purchaseOrders() {
      return this.$store.getters['purchaseOrders/subscription']
        .filter(po => po.classificationId === this.user?.classificationId)
        .filter(po => po.showOnWeb)
        .sort((a, b) => a.order - b.order);
    },
    user() {
      return this.$store.getters['user/get'];
    },
    userIsSubscribed() {
      const purchaseReceipts = this.$store.getters['purchaseReceipts/active'];
      return purchaseReceipts.some(pr => {
        const po = this.$store.getters['purchaseOrders/get'][pr.purchaseOrderId];
        return !po.isFree && po.classificationId === this.user?.classificationId;
      });
    },
  },
  created() {
    this.$watch('listener', (newValue, oldValue) => {
      if (newValue && !oldValue) {
        this.toggle();
      }
    });
    this.$watch('$route', (newRoute, oldRoute) => {
      if (this.active) {
        if (newRoute !== oldRoute) {
          this.hide();
        }
      }
    });
  },
  mounted() {
    this.selectedPurchaseOrder = this.preferredPurchaseOrder;
  },
  methods: {
    actionButtonText(po) {
      if (this.userHasActivePO(po)) {
        return 'Current Subscription';
      } else if (this.userIsSubscribed) {
        return `Switch to ${po.name}`;
      } else {
        return 'Start Your Free Trial';
      }
    },
    hide() {
      this.active = false;
      this.$store.dispatch('ui/forceBaseUpsellModal', { show: false, source: {} });
    },
    injectCookies() {
      this.partnerRef = this.$cookie.get('partner_ref');
      this.sc = this.$cookie.get('sc');
    },
    setSelectedPurchaseOrder(purchaseOrder) {
      this.selectedPurchaseOrder = purchaseOrder;
    },
    toggle() {
      this.active = true;
      const source = this.$store.getters['ui/upsellModalSource'];
      this.$analytics.events.viewedUpgradeScreen(source, this.preferredPurchaseOrder);
      this.injectCookies();
      this.$store.dispatch('ui/toggleUpsellModal', { show: false, source: {} });
    },
    triggerSubmit(index) {
      if (this.userIsSubscribed) {
        this.updateSubscription(this.purchaseOrders[index]);
      } else {
        let ref = this.$refs.submitForm[index].$el;
        ref.submit();
      }
    },
    updateSubscription(purchaseOrder) {
      const purchaseOrderIds = this.purchaseOrders.map(po => po.id);
      const purchaseReceipt = this.$store.getters['purchaseReceipts/active'].find(pr => purchaseOrderIds.includes(pr.purchaseOrderId));
      const params = {
        purchaseOrderId: purchaseOrder.id,
        purchaseReceiptId: purchaseReceipt.id,
      };
      const analyticsParams = {
        purchaseOrder,
      };
      this.$store.dispatch('user/changeSubscription', params).then(_ => {
        analyticsParams.status = 'Success';
        this.$analytics.events.clickedSwitchSubscription(analyticsParams);
        this.$notify({
          group: 'notifications',
          text: 'Subscription updated successfully!',
          type: 'success',
        });
        this.hide();
      }).catch(_ => {
        analyticsParams.status = 'Failure';
        this.$analytics.events.clickedSwitchSubscription(analyticsParams);
        this.$notify({
          group: 'notifications',
          text: 'An error occurred while updating subscription. Please contact support.',
          type: 'error',
        });
      });
    },
    userHasActivePO(purchaseOrder) {
      const purchaseReceipts = this.$store.getters['purchaseReceipts/active'];
      return purchaseReceipts.some(pr => pr.purchaseOrderId === purchaseOrder.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.base-upsell-modal-component {
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
  transition: opacity .3s ease;

  .backdrop {
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .content {
    height: 100%;
    width: 100%;
    background-color: #ffffff;
    position: relative;
    overflow: scroll;

    .tagline-container {
      align-items: center;
      background-color: #001C74;
      color: white;
      display: flex;
      flex-direction: column;
      height: 196px;
      justify-content: center;
      padding: 0 24px;
      text-align: center;

      .tagline {
        margin: 0;
        padding: 0;
        font-size: rem-calc(24);
      }

      .sub-tagline {
        margin-top: 8px;
      }
    }

    .purchase-options {
      height: calc(100% - 238px); // 192 for tagline, 46 for tabs

      .tabs {
        background-color: #001C74;
        display: flex;
        justify-content: space-evenly;

        .purchase-order-tab {
          flex: 1;
          background-color: #ffffff;
          border-radius: 12px 12px 0 0;
          border-top: 4px solid #7b7b7b;
          padding: 12px 0;
          text-align: center;
          color: #7b7b7b;
          font-weight: $bold;

          &.selected {
            color: #FF7E1E;
            border-color: #FF7E1E;
          }

          &:not(.selected) {
            box-shadow: inset 0px 2px 12px rgba(0, 0, 0, 0.15);
          }
        }
      }

      .purchase-details {
        box-sizing: border-box;
        width: 100%;
        display: flex;
        flex-direction: column;
        height: 100%;

        &.hidden {
          display: none;
        }

        .purchase-name {
          display: none;
        }

        .purchase-order {
          flex: 2;
          margin-bottom: auto;
        }

        .pricing {
          text-align: center;
          font-weight: $bold;
          font-size: 40px;
          margin: 0;

          .frequency-text {
            display: inline-block;
            margin-left: 4px;
            font-size: 20px;
          }
        }

        .trial-text {
          margin: 4px 0 0;
          text-align: center;
          font-weight: $semi-bold;
        }
      }
    }

    .action-button {
      background-color: #FF7E1E;
      border-radius: 66px;
      border: 2px solid transparent;
      color: #ffffff;
      display: block;
      font-weight: $semi-bold;
      margin: 20px auto 32px;
      padding: 18px;
      width: 95%;

      &.inverted {
        color: #000000;
        background-color: #FFFFFF;
        border-color: #FF7E1E;
      }

      &:disabled {
        opacity: .5;
      }
    }

    @media screen and (min-width: 64em) {
      width: 80%;
      max-width: 1160px;
      height: 648px;
      display: flex;

      .tagline-container {
        width: 33%;
        height: 100%;
        padding: 0 32px;
      }

      .purchase-options {
        display: flex;
        height: 100%;
        padding: 48px 16px 24px;
        box-sizing: border-box;
        justify-content: space-between;
        width: 100%;

        .tabs {
          display: none;
        }

        .purchase-details {
          border: 1px solid #d9d9d9;
          margin: 0 8px;
          height: auto;
          box-sizing: border-box;
          border-radius: 8px;
          border-top: 8px solid #7b7b7b;

          &.hidden {
            display: flex;
          }

          &.preferred {
            border-top-color: #FF7E1E;
          }

          .purchase-name {
            font-weight: $bold;
            display: initial;
            padding: 0 16px;
            margin-bottom: 0;
          }
        }
      }
    }
  }

  .close {
    align-items: center;
    background-color: #ffffff;
    border-radius: 50%;
    display: flex;
    height: 32px;
    justify-content: center;
    padding: 8px;
    position: absolute;
    right: 16px;
    top: 16px;
    width: 32px;

    @media screen and (min-width: 64em) {
      top: 8px;
    }
  }
}
</style>
